/**
 * Lifely API Service
 * Handles all API calls to the Cloudflare Worker backend
 */

import axios from 'axios';

// API Base URL - Use environment variable or default to production API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.lifely.guide';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage key
const TOKEN_KEY = 'lifely_auth_token';

// Clerk token provider - set by ClerkAuthBridge on mount
let clerkGetToken = null;
let logoutEventPending = false;

export function setTokenProvider(getTokenFn) {
  clerkGetToken = getTokenFn;
  logoutEventPending = false;
}

// Set auth token for all requests
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common['Authorization'];
  }
};

// Get stored token
export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Initialize token from storage on load
const storedToken = getStoredToken();
if (storedToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

// Request interceptor - attach a fresh Clerk token before every request
api.interceptors.request.use(async (config) => {
  if (clerkGetToken) {
    try {
      const token = await clerkGetToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (err) {
      console.error('Failed to get Clerk token:', err);
    }
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && clerkGetToken && !logoutEventPending) {
      logoutEventPending = true;
      setAuthToken(null);
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// ============================================
// Health Check
// ============================================
export const healthCheck = async () => {
  const response = await api.get('/api/health');
  return response.data;
};

// ============================================
// Auth API
// ============================================
export const authAPI = {
  // Login with Clerk token
  login: async (token) => {
    const response = await api.post('/api/auth/login', { token });
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await api.post('/api/auth/logout');
    setAuthToken(null);
    return response.data;
  },

  // Get current user
  me: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  // Sync user data from Clerk
  sync: async (token) => {
    const response = await api.post('/api/auth/sync', { token });
    return response.data;
  },
};

// ============================================
// Contacts API
// ============================================
export const contactsAPI = {
  // List all contacts
  list: async (params = {}) => {
    const response = await api.get('/api/contacts', { params });
    return response.data;
  },

  // Get single contact
  get: async (id) => {
    const response = await api.get(`/api/contacts/${id}`);
    return response.data;
  },

  // Create contact
  create: async (data) => {
    const response = await api.post('/api/contacts', data);
    return response.data;
  },

  // Update contact
  update: async (id, data) => {
    const response = await api.put(`/api/contacts/${id}`, data);
    return response.data;
  },

  // Delete contact
  delete: async (id) => {
    const response = await api.delete(`/api/contacts/${id}`);
    return response.data;
  },
};

// ============================================
// Maps API
// ============================================
export const mapsAPI = {
  // List all maps with entry counts
  list: async () => {
    const response = await api.get('/api/maps');
    return response.data;
  },

  // Get single map by type
  get: async (type) => {
    const response = await api.get(`/api/maps/${type}`);
    return response.data;
  },

  // Update map (notes, default wishes)
  update: async (type, data) => {
    const response = await api.put(`/api/maps/${type}`, data);
    return response.data;
  },

  // List entries for a map
  listEntries: async (type, params = {}) => {
    const response = await api.get(`/api/maps/${type}/entries`, { params });
    return response.data;
  },

  // Get single entry
  getEntry: async (type, id) => {
    const response = await api.get(`/api/maps/${type}/entries/${id}`);
    return response.data;
  },

  // Create entry
  createEntry: async (type, data) => {
    const response = await api.post(`/api/maps/${type}/entries`, data);
    return response.data;
  },

  // Update entry
  updateEntry: async (type, id, data) => {
    const response = await api.put(`/api/maps/${type}/entries/${id}`, data);
    return response.data;
  },

  // Delete entry
  deleteEntry: async (type, id) => {
    const response = await api.delete(`/api/maps/${type}/entries/${id}`);
    return response.data;
  },
};

// ============================================
// Will Status API
// ============================================
export const willStatusAPI = {
  // Get will status
  get: async () => {
    const response = await api.get('/api/will-status');
    return response.data;
  },

  // Update will status
  update: async (data) => {
    const response = await api.put('/api/will-status', data);
    return response.data;
  },
};

// ============================================
// POA Holders / Other Advisors / Formal Arrangements APIs
// (each endpoint returns { success: true, data: [...full list] })
// ============================================
export const poaHoldersAPI = {
  list: async () => (await api.get('/api/poa-holders')).data,
  create: async (data) => (await api.post('/api/poa-holders', data)).data,
  update: async (id, data) => (await api.put(`/api/poa-holders/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/api/poa-holders/${id}`)).data,
};

export const otherAdvisorsAPI = {
  list: async () => (await api.get('/api/other-advisors')).data,
  create: async (data) => (await api.post('/api/other-advisors', data)).data,
  update: async (id, data) => (await api.put(`/api/other-advisors/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/api/other-advisors/${id}`)).data,
};

export const formalArrangementsAPI = {
  list: async () => (await api.get('/api/formal-arrangements')).data,
  create: async (data) => (await api.post('/api/formal-arrangements', data)).data,
  update: async (id, data) => (await api.put(`/api/formal-arrangements/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/api/formal-arrangements/${id}`)).data,
};

// ============================================
// Children API
// ============================================
export const childrenAPI = {
  // List all children
  list: async () => {
    const response = await api.get('/api/children');
    return response.data;
  },

  // Get single child
  get: async (id) => {
    const response = await api.get(`/api/children/${id}`);
    return response.data;
  },

  // Create child
  create: async (data) => {
    const response = await api.post('/api/children', data);
    return response.data;
  },

  // Update child
  update: async (id, data) => {
    const response = await api.put(`/api/children/${id}`, data);
    return response.data;
  },

  // Delete child
  delete: async (id) => {
    const response = await api.delete(`/api/children/${id}`);
    return response.data;
  },
};

// ============================================
// Continuity API
// ============================================
export const continuityAPI = {
  // Get continuity settings
  get: async () => {
    const response = await api.get('/api/continuity');
    return response.data;
  },

  // Update continuity settings
  update: async (data) => {
    const response = await api.put('/api/continuity', data);
    return response.data;
  },

  // Send the verification email. body may include { source: 'wizard' | 'settings' }
  sendVerification: async (body = {}) => {
    const response = await api.post('/api/continuity/send-verification', body);
    return response.data;
  },

  // Activate continuity
  activate: async () => {
    const response = await api.post('/api/continuity/activate');
    return response.data;
  },

  // Deactivate continuity
  deactivate: async () => {
    const response = await api.post('/api/continuity/deactivate');
    return response.data;
  },

  // Manual check-in
  checkin: async () => {
    const response = await api.post('/api/continuity/checkin');
    return response.data;
  },

  // Get check-in history
  history: async (limit = 20) => {
    const response = await api.get('/api/continuity/history', { params: { limit } });
    return response.data;
  },

  // Nominees
  nominees: {
    list: async () => {
      const response = await api.get('/api/continuity/nominees');
      return response.data;
    },

    create: async (data) => {
      const response = await api.post('/api/continuity/nominees', data);
      return response.data;
    },

    update: async (id, data) => {
      const response = await api.put(`/api/continuity/nominees/${id}`, data);
      return response.data;
    },

    delete: async (id) => {
      const response = await api.delete(`/api/continuity/nominees/${id}`);
      return response.data;
    },
  },
};

// ============================================
// Sharing API
// ============================================
export const sharingAPI = {
  // List all share settings
  list: async () => {
    const response = await api.get('/api/sharing');
    return response.data;
  },

  // Get single share setting
  get: async (id) => {
    const response = await api.get(`/api/sharing/${id}`);
    return response.data;
  },

  // Create share setting
  create: async (data) => {
    const response = await api.post('/api/sharing', data);
    return response.data;
  },

  // Update share setting
  update: async (id, data) => {
    const response = await api.put(`/api/sharing/${id}`, data);
    return response.data;
  },

  // Delete share setting
  delete: async (id) => {
    const response = await api.delete(`/api/sharing/${id}`);
    return response.data;
  },
};

// ============================================
// Dashboard API
// ============================================
export const dashboardAPI = {
  // Get dashboard summary
  get: async () => {
    const response = await api.get('/api/dashboard');
    return response.data;
  },
};

// ============================================
// Activity API
// ============================================
export const activityAPI = {
  // List recent activity
  list: async (params = {}) => {
    const response = await api.get('/api/activity', { params });
    return response.data;
  },
};

// ============================================
// Account API
// ============================================
export const accountAPI = {
  // Request account deletion (sends confirmation email)
  requestDeletion: async () => {
    const response = await api.post('/api/account/request-deletion');
    return response.data;
  },
};

// ============================================
// Stripe Billing API
// ============================================
export const stripeAPI = {
  // Start a Stripe Checkout session. `plan` is 'monthly' | 'annual' (server maps to price).
  createCheckoutSession: async (plan) => {
    const response = await api.post('/api/stripe/create-checkout-session', {
      plan,
      successUrl: `${window.location.origin}/settings?upgraded=true`,
      cancelUrl: `${window.location.origin}/settings?cancelled=true`,
    });
    return response.data;
  },

  // Open the Stripe customer billing portal.
  createPortalSession: async () => {
    const response = await api.post('/api/stripe/create-portal-session');
    return response.data;
  },
};

// ============================================
// Vault Health API
// ============================================
export const vaultHealthAPI = {
  getHealth: async () => {
    const response = await api.get('/api/vault-health');
    return response.data;
  },
};

// ============================================
// AI API
// ============================================
export const aiAPI = {
  // Extract structured map-entry fields from a plain-English description.
  extractMapEntry: async (mapType, description) => {
    const response = await api.post('/api/ai/extract-map-entry', { mapType, description });
    return response.data;
  },

  // Generate a warm narrative summary from a client-assembled (redacted) vault digest.
  generateSummary: async (digest) => {
    const response = await api.post('/api/ai/generate-summary', { digest });
    return response.data;
  },
};

export default api;
